import { produce, WritableDraft } from "immer";
import {
  CHART_TYPES_BY_LABEL,
  ChartTypeName,
  getInitialReportConfig,
  isComparisonDateOperator,
  REPORT_PRESETS,
  ReportConfigV2,
  ReportFilterV2,
} from "./report-configuration.ts";
import {
  REPORT_CONFIG_ERROR_CODES,
  validateReportConfigV2,
} from "./reportConfigValidator.ts";
import { MeasureName, MEASURES } from "./measures.ts";
import {
  addTimeDimensionByDataset,
  DimensionName,
  DIMENSIONS,
  findTimeDimensionV2,
} from "./dimensions.ts";
import {
  addTimeFilterByDataset,
  FilterName,
  FILTERS,
  ReportFilterV2FromFilterDefinition,
} from "./filters.ts";
import { DatasetName } from "./constants.ts";
import { generateReportTitle } from "./common-utils.ts";
import {
  formatDate,
  getPeriodStartDate,
  isDateRangeLongerThanOneYear,
  toPlainDate,
} from "./dateUtils.ts";
import { DatabaseFieldConfig } from "./user.ts";

export type ReportDesignerState = {
  errorMessage: string | null;
  hasChanges: boolean;
  isInitializing: boolean;
  isError: boolean;
  newFilterDefinitionName: string | null;
  reportId: string | null;
  reportIsInSnapshotMode: boolean;
  reportConfig: ReportConfigV2;
  toastTitle: string | null;
  toastDescription: string | null;
  toastErrorIndex: number;
};

type AllowedAction =
  | "patchMetadata"
  | "resetState"
  | "reportHasBeenSaved"
  | "setChartSetting"
  | "setComparisonSettings"
  | "setDataset"
  | "setDimensions"
  | "setError"
  | "setFiltersFromNames"
  | "setIsInitializing"
  | "setMeasures"
  | "setPreset"
  | "setReportId"
  | "setReportConfig"
  | "setTimeCadence"
  | "setTitle"
  | "storeLegendItemChanges"
  | "toggleIncludeProjections"
  | "updateFilterByIndex"
  | "xAxisLabelClick"
  | "yAxisLabelClick";

export type ReportStateReducerActionType = {
  type: AllowedAction;
  payload?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export const initialReportDesignerState = (
  datasetName: DatasetName,
): ReportDesignerState => ({
  errorMessage: null,
  hasChanges: true,
  isInitializing: false,
  isError: false,
  newFilterDefinitionName: null,
  reportId: null,
  reportIsInSnapshotMode: false,
  reportConfig: getInitialReportConfig(datasetName),
  toastTitle: null,
  toastDescription: null,
  toastErrorIndex: 0,
});

let lastSavedState: string = "";

export const reducer = (
  state: ReportDesignerState,
  action: ReportStateReducerActionType,
  workspaceDatasetConfigs: Partial<Record<DatasetName, DatabaseFieldConfig>>,
): ReportDesignerState => {
  return produce(state, (draft: WritableDraft<ReportDesignerState>) => {
    switch (action.type) {
      case "xAxisLabelClick":
        if (
          draft.reportConfig.data.datasetName === "main" &&
          findTimeDimensionV2(draft.reportConfig.data)
        ) {
          // The clicked tick is the period END; the period runs backward one cadence
          const endDate = toPlainDate(action.payload)?.toString();
          if (!endDate) break;
          const startDate = getPeriodStartDate(
            endDate,
            draft.reportConfig.data.timeCadence,
          );

          draft.reportConfig.data.dimensions =
            draft.reportConfig.data.dimensions.filter(
              (dim) => !DIMENSIONS[dim]?.isTimeDimension,
            );

          const newFilter: ReportFilterV2 = {
            filterDefinitionName: "DATE",
            operator: "BETWEEN",
            value: startDate,
            secondaryValue: endDate,
          };
          const existingFilterIndex = draft.reportConfig.data.filters.findIndex(
            (f: ReportFilterV2) => f.filterDefinitionName === "DATE",
          );

          if (existingFilterIndex !== -1) {
            draft.reportConfig.data.filters.splice(
              existingFilterIndex,
              1,
              newFilter,
            );
          } else {
            draft.reportConfig.data.filters.push(newFilter);
          }

          ensureComparisonEligibility(draft);

          setToast(
            draft,
            `Date filter set to ${formatDate(startDate)} – ${formatDate(
              endDate,
            )} and the time dimension was removed.`,
          );
          setDirtyAiFlags(draft);
          updateTitleIfNotManuallySet(draft, workspaceDatasetConfigs);
        }

        break;

      case "yAxisLabelClick":
        if (isCultureChartFilterSetEligible(draft.reportConfig)) {
          const lastDim =
            draft.reportConfig.data.dimensions[
              draft.reportConfig.data.dimensions.length - 1
            ];
          // lastDim is either QUESTION_THEME or QUESTION_LABEL, so we want to find the filter
          // that corresponds to it and update its value to match the clicked label,
          // creating the filter if necessary
          const filterDefName =
            lastDim === "QUESTION_THEME" ? "QUESTION_THEME" : "QUESTION_LABEL";
          const existingFilterIndex = draft.reportConfig.data.filters.findIndex(
            (f: ReportFilterV2) => f.filterDefinitionName === filterDefName,
          );

          if (existingFilterIndex !== -1) {
            draft.reportConfig.data.filters.splice(existingFilterIndex, 1, {
              filterDefinitionName: filterDefName,
              operator: "IN",
              value: [action.payload],
            });
          } else {
            draft.reportConfig.data.filters.push({
              filterDefinitionName: filterDefName,
              operator: "IN",
              value: [action.payload],
            });
          }
          setToast(
            draft,
            `Filter for ${
              filterDefName === "QUESTION_THEME"
                ? "Question Theme"
                : "Question Label"
            } set to "${action.payload}".`,
          );
        }

        break;

      case "patchMetadata":
        draft.reportConfig.metadata = {
          ...draft.reportConfig.metadata,
          ...action.payload,
        };
        break;

      case "reportHasBeenSaved":
        lastSavedState = JSON.stringify(draft.reportConfig);
        draft.hasChanges = false;
        break;

      case "resetState":
        Object.assign(draft, initialReportDesignerState("main"));
        break;

      case "setChartSetting":
        {
          (draft.reportConfig.chart as Record<string, unknown>)[
            action.payload.field
          ] = action.payload.value;
          const validation = validateReportConfigV2({
            config: draft.reportConfig,
          });

          if (action.payload.field === "chartType") {
            // Guard: if switching to line chart, ensure there is a time dimension
            if (
              validation.errorCodes.includes(
                REPORT_CONFIG_ERROR_CODES.LINE_CHART_REQUIRES_TIME_DIMENSION,
              )
            ) {
              addTimeDimensionByDataset(draft.reportConfig);
              setToast(
                draft,
                "Line charts require a time dimension, so one has been added to your configuration.",
              );
            }
            // Guard: if switching from a multi-axis-capable chart to a chart that does not support
            // multiple axis groups, show a notice.
            if (
              ["Line", "Bar", "Stacked Bar"].includes(
                state.reportConfig.chart.chartType,
              ) &&
              validation.errorCodes.includes(
                REPORT_CONFIG_ERROR_CODES.MULTIPLE_AXIS_GROUPS_REQUIRE_LINE_OR_BAR_CHART,
              )
            ) {
              setToast(
                draft,
                "The current configuration requires a Line, Bar, or Stacked Bar chart to display multiple axes.",
              );
            }

            // Guard: if the new chart type needs a specific dimension, ensure it's present

            if (
              validation.errorCodes.includes(
                REPORT_CONFIG_ERROR_CODES.CHART_TYPE_REQUIRES_SPECIFIC_DIMENSION,
              )
            ) {
              const requiredDimension = validation.errors.find(
                (e) =>
                  e.code ===
                  REPORT_CONFIG_ERROR_CODES.CHART_TYPE_REQUIRES_SPECIFIC_DIMENSION,
              )?.offender as DimensionName;

              if (requiredDimension) {
                draft.reportConfig.data.dimensions = [requiredDimension];
                const chartConfig =
                  CHART_TYPES_BY_LABEL[action.payload.value as ChartTypeName];
                const dimensionsList =
                  chartConfig.requiresDimension
                    ?.map((d) => DIMENSIONS[d].label)
                    .join(" or ") ?? requiredDimension;
                setToast(
                  draft,
                  `The ${action.payload.value} chart type requires one of the following dimensions: ${dimensionsList}.`,
                );
              }
            }

            // Guard: if the new chart type requires a specific measure, use it
            const specificMeasureError = validation.errors.find(
              (e) =>
                e.code ===
                REPORT_CONFIG_ERROR_CODES.CHART_TYPE_REQUIRES_SPECIFIC_MEASURE,
            );

            if (specificMeasureError) {
              draft.reportConfig.data.measures = [
                specificMeasureError.offender as MeasureName,
              ];
              setToast(
                draft,
                `The ${action.payload.value} chart type requires the measure "${
                  MEASURES[specificMeasureError.offender as MeasureName].label
                }".`,
              );
            }
          }

          // set dirty AI flags if chart.limit was changed
          if (action.payload.field === "limit") {
            setDirtyAiFlags(draft);
          }

          updateTitleIfNotManuallySet(draft, workspaceDatasetConfigs);
          measureMechanics(draft);
        }
        break;

      case "setDataset":
        {
          if (state.reportConfig.data.datasetName === action.payload) {
            return; // no change
          }
          const newConfig = getInitialReportConfig(action.payload);
          draft.reportConfig = newConfig;
          measureMechanics(draft);
          cultureMechanics(draft);
        }
        break;

      case "setDimensions":
        {
          action.payload = dedupe(action.payload);
          const addedDimensions = action.payload.filter(
            (dimName: DimensionName) =>
              !draft.reportConfig.data.dimensions.includes(dimName),
          );

          const prospectiveNewState = {
              ...draft.reportConfig,
              data: {
                ...draft.reportConfig.data,
                dimensions: action.payload,
              },
            },
            validation = validateReportConfigV2({
              config: prospectiveNewState,
            });

          // Helper: when adding the Submitted Date dimension, switch to line chart
          if (addedDimensions.includes("SUBMIT_DATE")) {
            draft.reportConfig.chart.chartType = "Line";
            draft.reportConfig.data.timeCadence = "Week";
            setToast(
              draft,
              "The Submitted Date dimension looks better with a Line chart. The chart type has been switched to Line.",
            );
          }

          // Guard: If ANSWER_VALUE dimension is used, it must be the first dimension
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_MUST_COME_FIRST,
            )
          ) {
            const reorderedDimensions = action.payload.filter(
              (dimName: DimensionName) => dimName !== "ANSWER_VALUE",
            );
            action.payload = ["ANSWER_VALUE", ...reorderedDimensions];

            setToast(
              draft,
              "The Survey Answers dimension must be the first dimension. It has been moved to the first position.",
            );
          }

          // Guard: if ANSWER_VALUE dimension is used, it can only be used with one other dimension
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_ONLY_VALID_WITH_ONE_OTHER_DIMENSION,
            )
          ) {
            setToast(
              draft,
              "The Survey Answers dimension can only be used with one other dimension.",
            );
            return;
          }

          // Guard: if measure requires time dimension, ensure it's not being removed
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.MEASURE_REQUIRES_TIME_DIMENSION_OR_FILTER,
            )
          ) {
            // Handle time dimension removal
            setToast(
              draft,
              "The current selection requires a date dimension or filter.",
            );
            return;
          }

          // Guard: if Recruitment Stage (Historical) is selected, ensure Time in Stage or Applicant Count is selected.
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.RECRUITMENT_STAGE_HISTORICAL_REQUIRES_TIME_IN_STAGE_OR_APPLICANT_COUNT,
            )
          ) {
            // Handle missing Time in Stage or Applicant Count measures
            setToast(
              draft,
              "Recruitment Stage (Historical) requires the Time in Stage or Applicant Count measures.",
            );
            return;
          }

          // Guard: disallow DATE if datasetName === "attrition_drivers"
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ATTRITION_DRIVERS_DOES_NOT_SUPPORT_DATE_DIMENSION,
            )
          ) {
            action.payload = action.payload.filter(
              (dimName: DimensionName) => dimName !== "DATE",
            );
            setToast(
              draft,
              "The Date dimension is not applicable to the Attrition Drivers dataset and has been removed.",
            );
          }

          // Guard: Ensure only one time dimension is set
          const timeDimensionError = validation.errors.find(
            (err) =>
              err.code ===
              REPORT_CONFIG_ERROR_CODES.ONLY_ONE_TIME_DIMENSION_ALLOWED,
          );
          if (timeDimensionError) {
            action.payload = action.payload.filter(
              (dimName: DimensionName) =>
                dimName !== timeDimensionError.offender,
            );
            setToast(
              draft,
              "Only one time dimension can be used in a report. Additional time dimensions have been removed.",
            );
          }

          // Guard: do not allow more than 4 dimensions
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ONLY_FOUR_DIMENSIONS_ALLOWED,
            )
          ) {
            action.payload = action.payload.slice(0, 4);
            setToast(
              draft,
              "A maximum of four dimensions are supported in a report. Please remove one or more dimensions.",
            );
            return;
          }

          // Guard: if Time in Stage is selected, require the Recruitment Stage (Historical) dimension
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.TIME_IN_STAGE_REQUIRES_RECRUITMENT_STAGE_HISTORICAL,
            )
          ) {
            // Handle missing Recruitment Stage (Historical) dimension
            setToast(
              draft,
              "Time in Stage requires the Recruitment Stage (Historical) dimension.",
            );
            return;
          }

          // TERMINATION_REASON and TERMINATION_TYPE cannot be used together; if they are both
          // present, remove TERMINATION_REASON and show a toast
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.TERMINATION_TYPE_AND_TERMINATION_REASON_CANNOT_COEXIST,
            )
          ) {
            action.payload = action.payload.filter(
              (dimName: DimensionName) => dimName !== "TERMINATION_REASON",
            );
            setToast(
              draft,
              "Termination Type and Termination Reason cannot be used together. Termination Reason has been removed from the report dimensions.",
            );
          }

          if (
            state.reportConfig.chart.chartType === "Line" &&
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.LINE_CHART_REQUIRES_TIME_DIMENSION,
            )
          ) {
            // Switch to bar chart and set projection to false if no time dimension
            draft.reportConfig.chart.chartType = "Bar";
            draft.reportConfig.data.includeProjections = false;
            setToast(
              draft,
              "Line charts require a time dimension. The chart type has been switched to Bar.",
            );
          }

          // Guard: if removing all dimensions from a Funnel Chart,
          // set the chart type to Bar and show a toast explaining why

          if (
            draft.reportConfig.chart.chartType === "Funnel" &&
            action.payload.length === 0
          ) {
            draft.reportConfig.chart.chartType = "Bar";
            setToast(
              draft,
              "Funnel charts require at least one dimension. The chart type has been switched to Bar.",
            );
          }

          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.PIE_CHART_SUPPORTS_ONLY_ONE_DIMENSION,
            )
          ) {
            setToast(
              draft,
              "Pie charts silently aggregate multiple dimensions into single slices. Please switch to a different chart type to see multiple dimensions.",
            );
          }

          draft.reportConfig.data.dimensions = action.payload;
          ensureComparisonEligibility(draft);

          measureMechanics(draft);

          cultureMechanics(draft);

          updateTitleIfNotManuallySet(draft, workspaceDatasetConfigs);

          setDirtyAiFlags(draft);
        }
        break;

      case "setError":
        draft.isError = true;
        draft.errorMessage = action.payload;
        draft.isInitializing = false;
        break;

      case "setFiltersFromNames":
        // Filter configs are stored as FilterDefinition objects, but the report config
        // stores ReportFilterV2 objects which refer back to them by name. The report
        // designer UI MultiSelect tracks FilterDefinition objects, so we need to convert,
        // taking into account which filters were previously set. This action ignores
        // filters that exist already, and uses default values for any new ones.
        {
          // Guard: dedupe filter names
          action.payload = dedupe(action.payload);

          // Guard: maximum of 5 filters
          if (action.payload.length > 5) {
            setToast(
              draft,
              "A maximum of three filters are supported in a report. Please remove one or more filters.",
            );
            return;
          }

          const existingFilters = new Set(
            draft.reportConfig.data.filters.map(
              (filter: ReportFilterV2) => filter.filterDefinitionName,
            ),
          );
          const newFilters = action.payload.filter(
            (filterDefName: FilterName) => !existingFilters.has(filterDefName),
          );

          // Remove any filters that are no longer selected
          draft.reportConfig.data.filters =
            draft.reportConfig.data.filters.filter((f: ReportFilterV2) => {
              // Guard: do not remove required filters
              if (action.payload.includes(f.filterDefinitionName)) {
                return true;
              }
              const filterDef = FILTERS[f.filterDefinitionName];
              if (filterDef.required) {
                setToast(
                  draft,
                  `The "${filterDef.label}" filter is required and cannot be removed.`,
                );
                return true;
              } else {
                return false;
              }
            });
          // Add any new filters with default settings
          draft.newFilterDefinitionName = null;
          newFilters.forEach((filterName: FilterName) => {
            if (
              filterName === "ATTRITION_PROBABILITY_TIME_HORIZON" &&
              !draft.reportConfig.data.measures.includes(
                "ATTRITION_PROBABILITY",
              )
            ) {
              setToast(
                draft,
                "The Time Horizon filter requires the Attrition Probability measure. Please add that measure to your report.",
              );
              return;
            }
            const filterDef = FILTERS[filterName];
            draft.reportConfig.data.filters.push(
              ReportFilterV2FromFilterDefinition(filterDef),
            );
            draft.newFilterDefinitionName = filterName;
          });

          ensureComparisonEligibility(draft);
          setDirtyAiFlags(draft);
          cultureMechanics(draft);
        }
        break;

      case "setIsInitializing":
        draft.isInitializing = action.payload;
        break;

      case "setMeasures":
        {
          // Guard: dedupe measures
          action.payload = dedupe(action.payload);

          const prospectiveNewState = {
            ...draft.reportConfig,
            data: { ...draft.reportConfig.data, measures: action.payload },
          };
          const validation = validateReportConfigV2({
            config: prospectiveNewState,
          });

          // Guard: don't allow changes to measures if any is NOT_IMPLEMENTED
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.MEASURE_NOT_IMPLEMENTED,
            )
          ) {
            setToast(draft, "One or more measures are not implemented.");
            return;
          }

          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ATTRITION_DRIVERS_FORCES_SPECIAL_CONFIGURATION,
            )
          ) {
            // Guard: ATTRITION_DRIVERS can only be used with datasetName === "attrition_drivers"
            const newConfig = getInitialReportConfig("attrition_drivers");
            draft.reportConfig = newConfig;
            setToast(
              draft,
              "The Attrition Top Drivers measure requires a special configuration, which has been activated.",
            );
            return;
          }

          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.TIME_TO_FILL_DRIVERS_FORCES_SPECIAL_CONFIGURATION,
            )
          ) {
            // Guard: TIME_TO_FILL_DRIVERS can only be used with datasetName === "time_to_fill_drivers"
            const newConfig = getInitialReportConfig("time_to_fill_drivers");
            draft.reportConfig = newConfig;
            setToast(
              draft,
              "The Top Drivers of Longer Time to Fill measure requires a special configuration, which has been activated.",
            );
            return;
          }

          // Guard: only allow multiple axis groups for chart types that support them
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.MULTIPLE_AXIS_GROUPS_REQUIRE_LINE_OR_BAR_CHART,
            )
          ) {
            if (findTimeDimensionV2(draft.reportConfig.data)) {
              draft.reportConfig.chart.chartType = "Line";
              setToast(
                draft,
                "Your chart type has been switched to Line to accommodate multiple Y-axes.",
              );
            } else {
              draft.reportConfig.chart.chartType = "Bar";
              setToast(
                draft,
                "Your chart type has been switched to Bar to accommodate multiple Y-axes.",
              );
            }
          }
          // Guard: Attrition Probability requires Attrition Time Horizon filter
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ATTRITION_PROBABILITY_REQUIRES_ATTRITION_TIME_HORIZON_FILTER,
            )
          ) {
            setToast(
              draft,
              "Attrition Probability requires the Time Horizon filter.",
            );
            draft.reportConfig.data.filters.push({
              filterDefinitionName: "ATTRITION_PROBABILITY_TIME_HORIZON",
              operator: "SELECT",
              value: "attrition_6m", // default to 6 months
            });
          }

          // Guard: do not allow more than two distinct axis_groups, ever
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.ONLY_TWO_AXIS_GROUPS_ALLOWED,
            )
          ) {
            setToast(
              draft,
              "A maximum of two measure types (Y Axes) are supported in a report. Please remove one or more measures.",
            );
            return;
          }

          // Guard: if Time in Stage is selected, require the Recruitment Stage (Historical) dimension
          if (
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.TIME_IN_STAGE_REQUIRES_RECRUITMENT_STAGE_HISTORICAL,
            )
          ) {
            // Handle missing Recruitment Stage (Historical) dimension
            setToast(
              draft,
              "Time in Stage requires the Recruitment Stage (Historical) dimension.",
            );
            draft.reportConfig.data.dimensions.push(
              "RECRUITMENT_STAGE_HISTORICAL",
            );
          }

          const newMeasures = action.payload.filter(
            (measureName: MeasureName) =>
              !draft.reportConfig.data.measures.includes(measureName),
          );

          // We don't have a validator rule for this because it's a preference, not a requirement
          // and the validator operates on the entire config, it's not aware of what changed.
          if (
            newMeasures.some((m: MeasureName) => {
              const meas = MEASURES[m];
              if (meas && meas.preferChartType) {
                draft.reportConfig.chart.chartType = meas.preferChartType;
                return true;
              }
              return false;
            })
          ) {
            setToast(
              draft,
              "The chart type has been adjusted to better suit the newly added measure.",
            );
          }

          // Certain measures require a time slice of some kind, either a series (Date dimension) or
          // period (date filter).
          // Date filter is treated like a snapshot for purposes of report data SQL generation.
          // If there is no time dimension or date filter, automatically add a date filter for the last 365 days.
          const specialSinglePeriodMeasures = Object.values(MEASURES)
            .filter((meas) => meas.requiresTimeDimensionOrFilter)
            .map((meas) => meas.measure);
          const newMeasureIsSinglePeriod = newMeasures.some((m: MeasureName) =>
            specialSinglePeriodMeasures.includes(m),
          );
          if (
            newMeasureIsSinglePeriod &&
            !draft.reportConfig.data.filters.some(
              (f) => f.filterDefinitionName === "DATE",
            ) &&
            !draft.reportConfig.data.dimensions.includes("DATE")
          ) {
            draft.reportConfig.data.filters.push({
              filterDefinitionName: "DATE",
              operator: "LAST_365_DAYS",
              value: [],
            });
            setToast(
              draft,
              "A Date filter for the last 365 days has been automatically added to your report to support your Attrition measure.",
            );
          }

          // Guard: if AVG_SURVEY_SCORE is selected, do not allow SURVEY_SCORE, and vice versa
          if (
            newMeasures.includes("SURVEY_SCORE") &&
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.AVG_SURVEY_SCORE_NOT_ALLOWED_WITH_SURVEY_SCORE,
            )
          ) {
            setToast(
              draft,
              "The Survey Score measure cannot be used in the same report as the Average Survey Score measure. Please remove one of these measures.",
            );
            return;
          }

          if (
            newMeasures.includes("AVG_SURVEY_SCORE") &&
            validation.errorCodes.includes(
              REPORT_CONFIG_ERROR_CODES.AVG_SURVEY_SCORE_NOT_ALLOWED_WITH_SURVEY_SCORE,
            )
          ) {
            setToast(
              draft,
              "The Average Survey Score measure cannot be used in the same report as the Survey Score measure. Please remove one of these measures.",
            );
            return;
          }

          // For human users, when selecting compensation range top or bottom,
          // when no dimensions or filters are chosen, automatically set
          // timeCadence to quarter, add a DATE filter for the last year, and
          // set chartType to Line.
          if (
            (action.payload.includes("COMPENSATION_RANGE_TOP") ||
              action.payload.includes("COMPENSATION_RANGE_BOTTOM")) &&
            action.payload.length === 1
          ) {
            if (
              !draft.reportConfig.data.dimensions.length &&
              !draft.reportConfig.data.filters.length
            ) {
              draft.reportConfig.data.timeCadence = "Quarter";
              draft.reportConfig.data.dimensions.push("DATE");
              draft.reportConfig.data.filters.push({
                filterDefinitionName: "DATE",
                operator: "LAST_365_DAYS",
                value: [],
              });
              draft.reportConfig.chart.chartType = "Line";
            }
          }

          draft.reportConfig.data.measures = action.payload;

          measureMechanics(draft);

          cultureMechanics(draft);

          updateTitleIfNotManuallySet(draft, workspaceDatasetConfigs);

          setDirtyAiFlags(draft);
        }
        break;

      case "setPreset":
        {
          const maybePreset = REPORT_PRESETS[action.payload];
          if (maybePreset) {
            draft.reportConfig = JSON.parse(JSON.stringify(maybePreset.config));
            measureMechanics(draft);
            setDirtyAiFlags(draft);
          }
        }
        break;

      case "setReportId":
        if (state.reportId === action.payload) {
          return;
        }
        Object.assign(
          draft,
          initialReportDesignerState(draft.reportConfig.data.datasetName),
        );
        if (action.payload === null) {
          draft.isInitializing = false;
        }
        draft.reportId = action.payload;
        break;

      case "setReportConfig":
        draft.reportConfig = JSON.parse(JSON.stringify(action.payload));
        draft.isInitializing = false;
        draft.isError = false;
        draft.errorMessage = null;

        ensureComparisonEligibility(draft);
        measureMechanics(draft);
        updateTitleIfNotManuallySet(draft, workspaceDatasetConfigs);
        // this action occurs when loading a report or snapshot, or
        // when reverting changes, so we consider this the last saved state
        lastSavedState = JSON.stringify(draft.reportConfig);
        draft.hasChanges = false;

        break;

      case "setTimeCadence":
        draft.reportConfig!.data.timeCadence = action.payload;
        setDirtyAiFlags(draft);
        break;

      case "setTitle":
        draft.reportConfig.title = action.payload.title;
        draft.reportConfig._titleManuallySet = !!action.payload.manuallySet;
        break;

      case "storeLegendItemChanges":
        // We store legend item changes when they differ from the calculated defaults.
        // Color, label, and visibility are supported. chart.legendItems properties
        // will be used as overrides on top of the calculated seriesConfig if they are present.

        if (action.payload.seriesConfig.has(action.payload.key)) {
          const seriesConfigItem = action.payload.seriesConfig.get(
            action.payload.key,
          );

          if (seriesConfigItem) {
            if (
              (action.payload.field === "color" &&
                action.payload.newValue !== seriesConfigItem.color) ||
              (action.payload.field === "label" &&
                action.payload.newValue !== seriesConfigItem.label) ||
              (action.payload.field === "hidden" &&
                action.payload.newValue !== seriesConfigItem.hidden)
            ) {
              if (action.payload.key in draft.reportConfig.chart.legendItems) {
                draft.reportConfig.chart.legendItems[action.payload.key] = {
                  ...draft.reportConfig.chart.legendItems[action.payload.key],
                  [action.payload.field]: action.payload.newValue,
                };
              } else {
                draft.reportConfig.chart.legendItems[action.payload.key] = {
                  [action.payload.field]: action.payload.newValue,
                };
              }
            }
          }
        }

        break;

      case "toggleIncludeProjections":
        draft.reportConfig!.data.includeProjections = action.payload;
        setDirtyAiFlags(draft);
        break;

      case "setComparisonSettings":
        Object.assign(draft.reportConfig.data, action.payload);
        setDirtyAiFlags(draft);
        break;

      case "updateFilterByIndex":
        {
          const { index, newFilter } = action.payload;
          if (index >= 0 && index < draft.reportConfig.data.filters.length) {
            draft.reportConfig.data.filters[index] =
              newFilter as ReportFilterV2;
          }

          ensureComparisonEligibility(draft);
          setDirtyAiFlags(draft);
        }
        break;

      default:
        throw new Error(`Unhandled action type: ${action.type}`);
    }
    // After any action, check if there are unsaved changes
    const currentState = JSON.stringify(draft.reportConfig);
    draft.hasChanges = currentState !== lastSavedState;
  });
};

const updateTitleIfNotManuallySet = (
  draft: WritableDraft<ReportDesignerState>,
  workspaceDatasetConfigs: Partial<Record<DatasetName, DatabaseFieldConfig>>,
) => {
  if (!draft.reportConfig._titleManuallySet) {
    const fieldNameOverrides =
      workspaceDatasetConfigs?.[draft.reportConfig.data.datasetName]
        ?.fieldNameOverrides;
    draft.reportConfig.title = generateReportTitle(
      draft.reportConfig.data,
      fieldNameOverrides,
    );
  }
};

const setToast = (
  draft: ReportDesignerState,
  description: string,
  title: string = "Notice",
) => {
  draft.toastTitle = title;
  draft.toastDescription = description;
  draft.toastErrorIndex += 1;
};

const setDirtyAiFlags = (draft: WritableDraft<ReportDesignerState>) => {
  draft.reportConfig.metadata.ai_headline_dirty = true;
  draft.reportConfig.metadata.ai_summary_dirty = true;
};

const ensureComparisonEligibility = (
  draft: WritableDraft<ReportDesignerState>,
) => {
  const data = draft.reportConfig.data;

  if (!data.comparisonEnabled) return;

  const dateFilters = data.filters.filter((filter) => {
    const filterDefinition = FILTERS[filter.filterDefinitionName];
    return (
      filterDefinition.type === "date" &&
      filterDefinition.supportsSinglePeriodView
    );
  });

  if (dateFilters.length === 0) {
    data.comparisonEnabled = false;
    return;
  }
  const comparisonDateFilter = dateFilters[0];

  if (
    findTimeDimensionV2(data) ||
    dateFilters.length !== 1 ||
    (data.compareWith === "previousPeriod" &&
      !isComparisonDateOperator(comparisonDateFilter.operator)) ||
    (data.compareWith === "oneYearAgo" &&
      comparisonDateFilter.operator === "BETWEEN" &&
      isDateRangeLongerThanOneYear(
        comparisonDateFilter.value,
        comparisonDateFilter.secondaryValue,
      ))
  ) {
    data.comparisonEnabled = false;
  }
};

const cultureMechanics = (draft: WritableDraft<ReportDesignerState>) => {
  const validation = validateReportConfigV2({
    config: draft.reportConfig,
  });

  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.SURVEY_SCORE_REQUIRES_ANSWER_VALUE_DIMENSION,
    )
  ) {
    setToast(
      draft,
      "The Survey Score measure requires the Survey Answers dimension. The Survey Answers dimension has been added to your report.",
    );
    draft.reportConfig.data.dimensions.push("ANSWER_VALUE");
  }

  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_NOT_ALLOWED_FOR_AVG_SURVEY_SCORE,
    )
  ) {
    draft.reportConfig.data.dimensions =
      draft.reportConfig.data.dimensions.filter(
        (dim) => dim !== "ANSWER_VALUE",
      );
    setToast(
      draft,
      "The Average Survey Score measure cannot be used with the Survey Answers dimension. The Survey Answers dimension has been removed from your report.",
    );
  }
};

const measureMechanics = (draft: WritableDraft<ReportDesignerState>) => {
  const validation = validateReportConfigV2({
    config: draft.reportConfig,
  });

  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.MEASURE_DOES_NOT_SUPPORT_PROJECTIONS,
    ) &&
    draft.reportConfig.data.includeProjections
  ) {
    draft.reportConfig.data.includeProjections = false;
    setToast(
      draft,
      "One or more selected measures do not support projections, so projections have been disabled.",
    );
  }

  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.MEASURE_DOES_NOT_SUPPORT_DIMENSIONS,
    )
  ) {
    // Remove all dimensions if any selected measure does not support dimensions
    draft.reportConfig.data.dimensions = [];
    setToast(
      draft,
      "One or more selected measures do not support dimensions, so all dimensions have been removed.",
    );
  }

  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.MEASURE_REQUIRES_TIME_DIMENSION_OR_FILTER,
    )
  ) {
    setToast(
      draft,
      "A time filter has been added to your report data as one of your measures requires it.",
    );
    addTimeFilterByDataset(draft.reportConfig);
  }

  // If there is only one unique axisGroup, make sure chart.yAxisLabelOverride2 is cleared
  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.Y_AXIS_OVERRIDE_2_IS_CLEAR_IF_ONLY_ONE_AXIS_GROUP,
    )
  ) {
    draft.reportConfig.chart.yAxisLabelOverride2 = "";
  }

  // if the measure locks showCount to true, enforce that
  if (
    validation.errorCodes.includes(
      REPORT_CONFIG_ERROR_CODES.MEASURE_REQUIRES_SHOW_COUNT,
    )
  ) {
    draft.reportConfig.chart.showCount = true;
  }
};

const dedupe = (arr: any[]) => Array.from(new Set(arr)); // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * Helper function to determine if the current configuration is that
 * of a Culture report with Question Theme or Question Label as the Y axis
 * in order to support automatically setting that filter option when a user
 * clicks on it.
 */
const isCultureChartFilterSetEligible = (config: ReportConfigV2): boolean => {
  const lastDim = config.data.dimensions[config.data.dimensions.length - 1],
    hasCultureDataset = config.data.datasetName === "culture",
    hasQuestionThemeOrLabel =
      lastDim === "QUESTION_THEME" || lastDim === "QUESTION_LABEL";

  return hasCultureDataset && hasQuestionThemeOrLabel;
};
